import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import AddImage from "../Components/AddImage.jsx";
import RadioButton from "../Components/RadioButton.jsx";
import { ReactComponent as IconInfo } from "../Images/icon_info.svg";
import Layout from "../Layout/Layout";
import AlarmApply from "../Modals/AlarmApply.jsx";
import AlarmInputCheck from "../Modals/AlarmInputCheck.jsx";
import AlarmSimpleOk from "../Modals/AlarmSimpleOk.jsx";
import ImageUpload from "../Modals/ImageUpload.jsx";
import "../Styles/component.css";
import "../Styles/global.css";
import "../Styles/lists.css";
import { postFormData, postJson } from "../Utils/apiUtils";
import { showToast } from "../Utils/variables";

const formatDateValue = (value) => {
  if (!value) {
    return "";
  }

  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return String(value).slice(0, 10);
};

const getTodayText = () => formatDateValue(new Date());

const getBattleTypeLabel = (battleType) =>
  Number(battleType) === 0 ? "만보기(개인전)" : "만보기(팀전)";

const getOfficialTypeLabel = (officialType) =>
  Number(officialType) === 1 ? "공식 대회" : "일반 대회";

const getCountryLabel = (cCode) => {
  if (!cCode) {
    return "-";
  }

  if (cCode === "kr") {
    return "대한민국";
  }

  return String(cCode).toUpperCase();
};

const getProgressStatusLabel = (progressStatus) => {
  if (progressStatus === 0) {
    return "진행전";
  }

  if (progressStatus === 1) {
    return "진행중";
  }

  if (progressStatus === 2) {
    return "종료";
  }

  return "-";
};

const createInitialForm = () => ({
  meetWeId: null,
  image: null,
  title: "",
  content: "",
  battleType: 0,
  officialType: 0,
  confirmType: 0,
  maxNum: "",
  rawMaxNum: 0,
  joinCount: 0,
  pendingCount: 0,
  participantCount: 0,
  teamCount: 0,
  cStartTime: "",
  cEndTime: "",
  cCode: "",
  progressStatus: 0,
  canEdit: 0,
  canEditDate: 0,
  canEditMaxNum: 0,
  canEditOfficialType: 0,
  isUnlimited: 0,
});

const OurContestDetailContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditRoute = location.pathname.includes("/edit/");
  const [form, setForm] = useState(createInitialForm);
  const [isLoaded, setIsLoaded] = useState(false);
  const [clickImageUpload, setClickImageUpload] = useState(false);
  const [clickApply, setClickApply] = useState(false);
  const [inputCheckFail, setInputCheckFail] = useState(false);
  const [clickMaxNumBlocked, setClickMaxNumBlocked] = useState(false);
  const lastContestDetailRequestKey = useRef(null);

  const isEditMode = isEditRoute && form.canEdit === 1;
  const todayText = getTodayText();

  const participantLabel = useMemo(() => {
    if (form.progressStatus === 0) {
      return "참가 신청 인원";
    }

    if (form.confirmType === 1 && form.pendingCount > 0) {
      return "현재 참여/요청 인원";
    }

    return "현재 참여 인원";
  }, [form.confirmType, form.pendingCount, form.progressStatus]);

  const participantDisplayCount = useMemo(() => {
    if (form.progressStatus === 0 || (form.confirmType === 1 && form.pendingCount > 0)) {
      return form.participantCount;
    }

    return form.joinCount;
  }, [form.confirmType, form.joinCount, form.participantCount, form.pendingCount, form.progressStatus]);

  const getDetail = async () => {
    try {
      const body = { meetWeId: id };
      const response = await postJson("/dashboard/meetWe/getDetail", body, navigate);

      if (response.code !== 200) {
        showToast(response.message ?? "우리끼리 대회 정보를 불러오지 못했습니다.", "error");
        navigate("/meetWe");
        return;
      }

      const data = response.data;

      if (isEditRoute && data.canEdit !== 1) {
        showToast("수정할 수 없는 대회입니다.", "error");
        navigate(`/meetWe/detail/${id}`, { replace: true });
        return;
      }

      setForm({
        meetWeId: data.meetWeId,
        image: data.image,
        title: data.title ?? "",
        content: data.content ?? "",
        battleType: Number(data.battleType ?? 0),
        officialType: Number(data.officialType ?? 0),
        confirmType: Number(data.confirmType ?? 0),
        maxNum: data.maxNum ?? 0,
        rawMaxNum: Number(data.rawMaxNum ?? 0),
        joinCount: Number(data.joinCount ?? 0),
        pendingCount: Number(data.pendingCount ?? 0),
        participantCount: Number(data.participantCount ?? 0),
        teamCount: Number(data.teamCount ?? 0),
        cStartTime: formatDateValue(data.cStartTime),
        cEndTime: formatDateValue(data.cEndTime),
        cCode: data.cCode ?? "",
        progressStatus: Number(data.progressStatus ?? 0),
        canEdit: Number(data.canEdit ?? 0),
        canEditDate: Number(data.canEditDate ?? 0),
        canEditMaxNum: Number(data.canEditMaxNum ?? 0),
        canEditOfficialType: Number(data.canEditOfficialType ?? 0),
        isUnlimited: Number(data.isUnlimited ?? 0),
      });
      setIsLoaded(true);
    } catch (error) {
      console.log("우리끼리대회 상세 가져오기 중 에러:", error);
      throw error;
    }
  };

  useEffect(() => {
    const requestKey = JSON.stringify({ id, isEditRoute });

    if (lastContestDetailRequestKey.current === requestKey) {
      return;
    }

    lastContestDetailRequestKey.current = requestKey;
    getDetail();
  }, [id, isEditRoute]);

  const handleCloseModals = () => {
    setClickImageUpload(false);
    setClickApply(false);
    setInputCheckFail(false);
    setClickMaxNumBlocked(false);
  };

  const handleMaxNumChange = (value) => {
    const numbersOnly = String(value).replace(/[^0-9]/g, "");
    setForm((prev) => ({ ...prev, maxNum: numbersOnly }));
  };

  const handleUpdate = async () => {
    setClickApply(false);

    const hasImage = Array.isArray(form.image)
      ? form.image.length > 0
      : Boolean(form.image);

    if (!form.title.trim() || !form.content.trim() || !hasImage) {
      setInputCheckFail(true);
      return;
    }

    const maxNumValue = form.isUnlimited ? 0 : Number(form.maxNum);

    if (!form.isUnlimited && (!Number.isInteger(maxNumValue) || maxNumValue <= 0)) {
      setInputCheckFail(true);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("meetWeId", form.meetWeId);
      formData.append("title", form.title.trim());
      formData.append("content", form.content.trim());
      formData.append("maxNum", String(maxNumValue));
      formData.append("cStartTime", form.cStartTime);
      formData.append("cEndTime", form.cEndTime);
      formData.append("officialType", String(form.officialType));

      if (Array.isArray(form.image) && form.image[0]) {
        formData.append("image", form.image[0]);
      }

      const response = await postFormData("/dashboard/meetWe/update", formData, navigate);

      if (response.code === 200) {
        navigate("/meetWe", {
          state: {
            toast_enable: true,
            toast_message: "우리끼리 대회가 수정되었습니다.",
          },
        });
        return;
      }

      if (response.errorType === "MAX_NUM_BELOW_PARTICIPANTS") {
        setClickMaxNumBlocked(true);
        return;
      }

      showToast(response.message ?? "우리끼리 대회 수정 중 오류가 발생했습니다.", "error");
    } catch (error) {
      console.log("우리끼리대회 수정 중 에러:", error);
      throw error;
    }
  };

  if (!isLoaded) {
    return null;
  }

  return (
    <div>
      {clickImageUpload && isEditMode && (
        <ImageUpload
          title="대회 이미지 업로드"
          info="앱에서 등록한 이미지를 기본으로 사용하며, 다른 이미지로 교체할 수 있습니다."
          handleClickX={handleCloseModals}
          imageUpload={(value) =>
            setForm((prev) => ({
              ...prev,
              image:
                Array.isArray(value) && value.length > 0 ? value : prev.image,
            }))
          }
          pageName="OurContestDetail"
          previewImg={form.image}
        />
      )}
      {clickApply && isEditMode && (
        <AlarmApply
          title="우리끼리 대회를 수정하시겠습니까?"
          subtitle="변경된 내용은 랭킹마라톤 앱과 대시보드에 즉시 반영됩니다."
          handleApply={handleUpdate}
          handleClickX={handleCloseModals}
        />
      )}
      {inputCheckFail && <AlarmInputCheck handleClickX={handleCloseModals} />}
      {clickMaxNumBlocked && (
        <AlarmSimpleOk
          title="최대 참가 인원 변경이 불가해요"
          description={
            "현재 참여 중이거나 참가 요청 중인 인원이 있어,\n그보다 작은 인원으로는 변경할 수 없습니다.\n현재 참가 인원 이상으로 설정해 주세요."
          }
          handleClickX={handleCloseModals}
        />
      )}

      <ContestDetailBox>
        <div className="title-in-box">
          {isEditMode ? "우리끼리 대회 수정" : "우리끼리 대회 상세"}
        </div>
        <div className="hrz-wrapper">
          <div
            className="text-for-notice"
            style={{ marginLeft: "auto", marginTop: "10px" }}
          >
            {isEditMode
              ? "*표시는 필수 입력 항목입니다."
              : "*상세 페이지에서는 대회 정보를 확인할 수 있습니다."}
          </div>
        </div>

        <AddImage
          title="대회 이미지"
          isRequired={isEditMode}
          noticeText="앱에서 등록한 이미지를 기본으로 사용합니다."
          handleClickUpload={() => setClickImageUpload(true)}
          previewImg={form.image}
          showExample={false}
          readOnly={!isEditMode}
        />

        <FieldSection>
          <FieldLabel>
            카테고리
            {isEditMode && <span>*</span>}
          </FieldLabel>
          {isEditMode ? (
            <RadioGroupBox>
              <RadioButton
                option1="일반 대회"
                option2="공식 대회"
                selectedOption={form.officialType}
                optionChange={(value) =>
                  setForm((prev) => ({ ...prev, officialType: value }))
                }
                disabled={!form.canEditOfficialType}
              />
            </RadioGroupBox>
          ) : (
            <ReadOnlyBox>{getOfficialTypeLabel(form.officialType)}</ReadOnlyBox>
          )}
        </FieldSection>

        <FieldRow>
          <FieldColumn>
            <FieldLabel>진행 상태</FieldLabel>
            <ReadOnlyBox>{getProgressStatusLabel(form.progressStatus)}</ReadOnlyBox>
          </FieldColumn>
          <FieldColumn>
            <FieldLabel>대회 종류</FieldLabel>
            <ReadOnlyBox>{getBattleTypeLabel(form.battleType)}</ReadOnlyBox>
          </FieldColumn>
        </FieldRow>

        <FieldRow>
          <FieldColumn>
            <FieldLabel>
              시작일
              {isEditMode && form.canEditDate === 1 && <span>*</span>}
            </FieldLabel>
            {isEditMode ? (
              <DateInput
                type="date"
                value={form.cStartTime}
                min={todayText}
                disabled={form.canEditDate !== 1}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    cStartTime: event.target.value,
                    cEndTime:
                      prev.cEndTime && prev.cEndTime < event.target.value
                        ? event.target.value
                        : prev.cEndTime,
                  }))
                }
              />
            ) : (
              <ReadOnlyBox>{form.cStartTime}</ReadOnlyBox>
            )}
          </FieldColumn>
          <FieldColumn>
            <FieldLabel>
              종료일
              {isEditMode && form.canEditDate === 1 && <span>*</span>}
            </FieldLabel>
            {isEditMode ? (
              <DateInput
                type="date"
                value={form.cEndTime}
                min={form.cStartTime || todayText}
                disabled={form.canEditDate !== 1}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, cEndTime: event.target.value }))
                }
              />
            ) : (
              <ReadOnlyBox>{form.cEndTime}</ReadOnlyBox>
            )}
          </FieldColumn>
        </FieldRow>

        {isEditMode && form.canEditDate !== 1 && (
          <NoticeText>진행 중인 대회는 시작일과 종료일을 수정할 수 없습니다.</NoticeText>
        )}

        <FieldRow>
          <FieldColumn>
            <FieldHeader>
              <FieldLabel>{participantLabel}</FieldLabel>
            </FieldHeader>
            <ReadOnlyBox>{participantDisplayCount.toLocaleString()}명</ReadOnlyBox>
          </FieldColumn>
          <FieldColumn>
            <FieldHeader>
              <FieldLabel>
                최대 참가 인원 설정
                {isEditMode && form.canEditMaxNum === 1 && <span>*</span>}
              </FieldLabel>
              {form.battleType === 1 && (
                <FieldHelperText>
                  <IconInfo width="16px" height="16px" fill="#888888" />
                  <span>팀전은 팀당 최대 참여 인원 수</span>
                </FieldHelperText>
              )}
            </FieldHeader>
            {isEditMode ? (
              <TextInput
                type="text"
                inputMode="numeric"
                value={form.isUnlimited ? "제한없음" : form.maxNum}
                disabled={form.canEditMaxNum !== 1}
                placeholder="최대 참가 인원 설정"
                onChange={(event) => handleMaxNumChange(event.target.value)}
              />
            ) : (
              <ReadOnlyBox>
                {form.isUnlimited ? "제한없음" : `${Number(form.maxNum).toLocaleString()}명`}
              </ReadOnlyBox>
            )}
          </FieldColumn>
        </FieldRow>

        {isEditMode && (
          <>
            {form.canEditMaxNum !== 1 && form.isUnlimited === 1 && (
              <NoticeText>앱에서 제한없음으로 등록한 대회는 최대 참가 인원을 수정할 수 없습니다.</NoticeText>
            )}
          </>
        )}

        <FieldSection>
          <FieldLabel>국가</FieldLabel>
          <ReadOnlyBox>{getCountryLabel(form.cCode)}</ReadOnlyBox>
        </FieldSection>

        <FieldSection>
          <FieldLabel>
            대회명
            {isEditMode && <span>*</span>}
          </FieldLabel>
          {isEditMode ? (
            <TextInput
              type="text"
              value={form.title}
              maxLength={60}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, title: event.target.value }))
              }
            />
          ) : (
            <ReadOnlyBox>{form.title}</ReadOnlyBox>
          )}
        </FieldSection>

        <FieldSection>
          <FieldLabel>
            내용
            {isEditMode && <span>*</span>}
          </FieldLabel>
          {isEditMode ? (
            <TextArea
              value={form.content}
              maxLength={1000}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, content: event.target.value }))
              }
            />
          ) : (
            <ReadOnlyBox style={{ minHeight: "160px", whiteSpace: "pre-wrap" }}>
              {form.content}
            </ReadOnlyBox>
          )}
        </FieldSection>

        <ButtonRow>
          <div
            className="cancelBtn center"
            style={{ marginLeft: "auto" }}
            onClick={() => navigate("/meetWe")}
          >
            취소
          </div>
          {isEditMode && (
            <div
              className="applyBtn center"
              style={{ marginLeft: "14px" }}
              onClick={() => setClickApply(true)}
            >
              수정
            </div>
          )}
        </ButtonRow>
      </ContestDetailBox>
    </div>
  );
};

const ContestDetailBox = styled.div`
  width: 760px;
  height: auto;
  border-radius: 20px;
  margin-left: 413px;
  margin-top: 30px;
  margin-bottom: 30px;
  padding: 30px;
  background-color: #ffffff;
`;

const FieldSection = styled.div`
  margin-top: 30px;
`;

const RadioGroupBox = styled.div`
  margin-top: 14px;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 30px;
`;

const FieldColumn = styled.div`
  flex: 1;
`;

const FieldHeader = styled.div`
  min-height: 48px;
`;

const FieldLabel = styled.div`
  font-family: "notoSans-bold";
  font-size: 18px;
  color: #222222;

  > span {
    color: #1167ed;
    margin-left: 2px;
  }
`;

const FieldHelperText = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #888888;
  font-size: 14px;
  line-height: 1.4;
`;

const ReadOnlyBox = styled.div`
  width: 100%;
  min-height: 60px;
  margin-top: 14px;
  padding: 18px 20px;
  border-radius: 10px;
  background-color: #f5f5f5;
  color: #222222;
  box-sizing: border-box;
  line-height: 1.5;
`;

const TextInput = styled.input`
  width: 100%;
  height: 60px;
  margin-top: 14px;
  padding: 0 20px;
  border: 1px solid #e4e4e4;
  border-radius: 10px;
  box-sizing: border-box;
  font-size: 16px;
  color: #222222;

  &:disabled {
    background-color: #f5f5f5;
    color: #888888;
    cursor: not-allowed;
  }
`;

const DateInput = styled(TextInput)`
  padding-right: 16px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 160px;
  margin-top: 14px;
  padding: 20px;
  border: 1px solid #e4e4e4;
  border-radius: 10px;
  box-sizing: border-box;
  font-size: 16px;
  color: #222222;
  line-height: 1.5;
  resize: vertical;
`;

const NoticeText = styled.div`
  margin-top: 8px;
  color: #888888;
  font-size: 14px;
  line-height: 1.5;
`;

const ButtonRow = styled.div`
  display: flex;
  margin-top: 30px;
`;

const OurContestDetail = () => {
  return <Layout page={<OurContestDetailContent />} />;
};

export default OurContestDetail;
